// Merkan · Geführter Zugriff: Zustand (kleiner Store mit useSyncExternalStore) und Fortschritt.
//
// Öffentliche API (auch als Ereignisse auf window nutzbar):
//   tourStarten(id, ab?)          Ereignis 'merkan:tour-starten'   detail { id, ab? }
//   tourFortsetzen(id?)           Ereignis 'merkan:tour-fortsetzen' detail { id? }
//   hilfeOeffnen()                Ereignis 'merkan:hilfe-oeffnen'
// Fortschritt je Benutzer und Mandant in anleitung_fortschritt (RLS: nur eigene Zeilen), dazu ein Spiegel im
// localStorage, damit der Wiedereinstieg sofort nach dem Neuladen am exakt gleichen Schritt klappt.
import { useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';
import { anleitungenLaden, anleitungFinden } from './anleitungen';

export type FortschrittStatus = 'laufend' | 'pausiert' | 'beendet';
export interface Fortschritt { schritt: number; pfad: string | null; status: FortschrittStatus; aktualisiert: string }

export interface AktiveTour {
  id: string;
  schritt: number;
  /** Gespeicherter Pfad beim Einstieg (Start mit ab, Fortsetzen, Neuladen); nur für den ersten Schritt gültig */
  pfad: string | null;
  /** true beim ersten Schritt nach Start oder Wiederaufnahme */
  einsprung: boolean;
  /** Richtung des letzten Wechsels (zum Überspringen optionaler Schritte) */
  richtung: 1 | -1;
  /** Zähler, damit derselbe Schritt erneut aufgelöst werden kann */
  lauf: number;
}

interface Zustand {
  mandantId: string | null;
  userId: string | null;
  fortschritt: Record<string, Fortschritt>;
  geladen: boolean;
  aktiv: AktiveTour | null;
  panelOffen: boolean;
}

let zustand: Zustand = { mandantId: null, userId: null, fortschritt: {}, geladen: false, aktiv: null, panelOffen: false };
const hoerer = new Set<() => void>();
function setzen(teil: Partial<Zustand>) {
  zustand = { ...zustand, ...teil };
  hoerer.forEach((h) => h());
}

export function useTour(): Zustand {
  return useSyncExternalStore((h) => { hoerer.add(h); return () => { hoerer.delete(h); }; }, () => zustand);
}
export const tourZustand = () => zustand;

// ---------------------------------------------------------------------------
// Spiegel im localStorage
// ---------------------------------------------------------------------------
const spiegelSchluessel = () => (zustand.userId && zustand.mandantId ? `merkan.tour.${zustand.userId}.${zustand.mandantId}` : null);

function spiegelLesen(): Record<string, Fortschritt> {
  const k = spiegelSchluessel();
  if (!k) return {};
  try {
    const roh = localStorage.getItem(k);
    const o = roh ? JSON.parse(roh) : null;
    return o && typeof o === 'object' ? (o as Record<string, Fortschritt>) : {};
  } catch { return {}; }
}
function spiegelSchreiben() {
  const k = spiegelSchluessel();
  if (!k) return;
  try { localStorage.setItem(k, JSON.stringify(zustand.fortschritt)); } catch { /* ohne Speicher nur Datenbank */ }
}

// ---------------------------------------------------------------------------
// Datenbank: je Anleitung höchstens ein Schreibvorgang gleichzeitig, der jeweils neueste Stand gewinnt
// ---------------------------------------------------------------------------
const inArbeit = new Set<string>();
const wartend = new Map<string, Fortschritt | null>();

function dbSchreiben(id: string, f: Fortschritt | null) {
  wartend.set(id, f);
  if (inArbeit.has(id)) return;
  void dbAbarbeiten(id);
}
async function dbAbarbeiten(id: string) {
  const { mandantId, userId } = zustand;
  if (!mandantId || !userId) { wartend.delete(id); return; }
  inArbeit.add(id);
  try {
    while (wartend.has(id)) {
      const f = wartend.get(id) ?? null;
      wartend.delete(id);
      try {
        if (f) {
          await supabase.from('anleitung_fortschritt').upsert(
            { user_id: userId, mandant_id: mandantId, anleitung_id: id, schritt: f.schritt, pfad: f.pfad, status: f.status },
            { onConflict: 'user_id,mandant_id,anleitung_id' },
          );
        } else {
          await supabase.from('anleitung_fortschritt').delete().eq('mandant_id', mandantId).eq('anleitung_id', id);
        }
      } catch { /* Spiegel bleibt; nächster Wechsel versucht es erneut */ }
    }
  } finally { inArbeit.delete(id); }
}

function fortschrittSetzen(id: string, f: Fortschritt | null, db = true) {
  const neu = { ...zustand.fortschritt };
  if (f) neu[id] = f; else delete neu[id];
  setzen({ fortschritt: neu });
  spiegelSchreiben();
  if (db) dbSchreiben(id, f);
}

const jetzt = () => new Date().toISOString();
const aktuellerPfad = () => (typeof window === 'undefined' ? null : window.location.pathname + window.location.search);

// ---------------------------------------------------------------------------
// Einrichten je Benutzer und Mandant (Rahmen bzw. HilfeSymbol ruft das beim Start auf)
// ---------------------------------------------------------------------------
let ladeLauf = 0;
export function tourEinrichten(mandantId: string | null, userId: string | null) {
  if (zustand.mandantId === mandantId && zustand.userId === userId) return;
  const lauf = ++ladeLauf;
  zustand = { ...zustand, mandantId, userId, fortschritt: {}, geladen: false, aktiv: null, panelOffen: false };
  if (!mandantId || !userId) { setzen({}); return; }
  // 1. sofort aus dem Spiegel: laufende Tour direkt fortsetzen
  const lokal = spiegelLesen();
  const laufend = neuesteMit(lokal, 'laufend');
  setzen({
    fortschritt: lokal,
    aktiv: laufend ? { id: laufend[0], schritt: laufend[1].schritt, pfad: laufend[1].pfad, einsprung: true, richtung: 1, lauf: 0 } : null,
  });
  // 2. Datenbank nachladen und zusammenführen (neuerer Stand gewinnt)
  void (async () => {
    try {
      const { data, error } = await supabase.from('anleitung_fortschritt')
        .select('anleitung_id, schritt, pfad, status, aktualisiert').eq('mandant_id', mandantId);
      if (lauf !== ladeLauf || error) { if (lauf === ladeLauf) setzen({ geladen: true }); return; }
      const zusammen: Record<string, Fortschritt> = { ...zustand.fortschritt };
      for (const z of (data ?? []) as { anleitung_id: string; schritt: number; pfad: string | null; status: FortschrittStatus; aktualisiert: string }[]) {
        const l = zusammen[z.anleitung_id];
        if (!l || Date.parse(z.aktualisiert) > Date.parse(l.aktualisiert)) {
          zusammen[z.anleitung_id] = { schritt: z.schritt, pfad: z.pfad, status: z.status, aktualisiert: z.aktualisiert };
        }
      }
      // Nur eine Tour läuft: weitere „laufend“ (anderes Gerät) gelten als pausiert
      const fuehrend = zustand.aktiv?.id ?? neuesteMit(zusammen, 'laufend')?.[0] ?? null;
      for (const [id, f] of Object.entries(zusammen)) if (f.status === 'laufend' && id !== fuehrend) zusammen[id] = { ...f, status: 'pausiert' };
      const teil: Partial<Zustand> = { fortschritt: zusammen, geladen: true };
      if (!zustand.aktiv && fuehrend && zusammen[fuehrend]) {
        const f = zusammen[fuehrend];
        teil.aktiv = { id: fuehrend, schritt: f.schritt, pfad: f.pfad, einsprung: true, richtung: 1, lauf: 0 };
      }
      setzen(teil);
      spiegelSchreiben();
    } catch { if (lauf === ladeLauf) setzen({ geladen: true }); }
  })();
}

function neuesteMit(f: Record<string, Fortschritt>, status: FortschrittStatus): [string, Fortschritt] | null {
  let best: [string, Fortschritt] | null = null;
  for (const e of Object.entries(f)) if (e[1].status === status && (!best || Date.parse(e[1].aktualisiert) > Date.parse(best[1].aktualisiert))) best = e;
  return best;
}

/** Neueste pausierte Tour (für Symbol und Fortsetzen-Karte) */
export const pausierteTour = (f: Record<string, Fortschritt> = zustand.fortschritt) => neuesteMit(f, 'pausiert');
/** Symbolzustand: pausiert vor beendet, sonst keines */
export function symbolArt(f: Record<string, Fortschritt> = zustand.fortschritt): 'pause' | 'buch' | null {
  const werte = Object.values(f);
  if (werte.some((x) => x.status === 'pausiert')) return 'pause';
  if (werte.some((x) => x.status === 'beendet')) return 'buch';
  return null;
}

// ---------------------------------------------------------------------------
// Öffentliche Funktionen
// ---------------------------------------------------------------------------
/** Startet eine Tour (optional ab Schritt `ab`, nullbasiert). Nur, wenn die Anleitung freigeschaltet ist. */
export async function tourStarten(id: string, ab = 0, pfad: string | null = null): Promise<boolean> {
  const { mandantId } = zustand;
  if (!mandantId) return false;
  const a = anleitungFinden(mandantId, id) ?? (await anleitungenLaden(mandantId)).find((x) => x.id === id) ?? null;
  if (!a || !a.freigeschaltet || !a.schritte.length) return false;
  const schritt = Math.max(0, Math.min(a.schritte.length - 1, Math.floor(ab)));
  // Eine andere laufende Tour wird pausiert, ihr Stand bleibt erhalten
  const vorher = zustand.aktiv;
  if (vorher && vorher.id !== id) {
    const f = zustand.fortschritt[vorher.id];
    fortschrittSetzen(vorher.id, { schritt: vorher.schritt, pfad: f?.pfad ?? aktuellerPfad(), status: 'pausiert', aktualisiert: jetzt() });
  }
  setzen({ aktiv: { id, schritt, pfad, einsprung: true, richtung: 1, lauf: (vorher?.lauf ?? 0) + 1 }, panelOffen: false });
  fortschrittSetzen(id, { schritt, pfad: pfad ?? aktuellerPfad(), status: 'laufend', aktualisiert: jetzt() });
  return true;
}

/** Setzt die (neueste) pausierte Tour genau an ihrem Schritt und Pfad fort */
export async function tourFortsetzen(id?: string): Promise<boolean> {
  const ziel = id ? (zustand.fortschritt[id] ? [id, zustand.fortschritt[id]] as const : null) : pausierteTour();
  if (!ziel) return false;
  return tourStarten(ziel[0], ziel[1].schritt, ziel[1].pfad);
}

/** Wechselt zum Schritt n der aktiven Tour (Engine) */
export function schrittSetzen(n: number) {
  const a = zustand.aktiv;
  if (!a) return;
  setzen({ aktiv: { ...a, schritt: n, pfad: null, einsprung: false, richtung: n >= a.schritt ? 1 : -1, lauf: a.lauf + 1 } });
  fortschrittSetzen(a.id, { schritt: n, pfad: aktuellerPfad(), status: 'laufend', aktualisiert: jetzt() });
}

/** Merkt den aktuellen Pfad zum laufenden Schritt (bei Seitenwechsel innerhalb eines Schritts) */
let pfadTakt: number | undefined;
export function pfadMerken(pfad: string) {
  const a = zustand.aktiv;
  if (!a) return;
  const f = zustand.fortschritt[a.id];
  if (!f || f.pfad === pfad || f.schritt !== a.schritt) return;
  const neu = { ...f, pfad, aktualisiert: jetzt() };
  fortschrittSetzen(a.id, neu, false);
  window.clearTimeout(pfadTakt);
  pfadTakt = window.setTimeout(() => { const g = zustand.fortschritt[a.id]; if (g) dbSchreiben(a.id, g); }, 400);
}

/** X oder Esc: Tour pausieren (Schritt und Pfad bleiben) */
export function tourAbbrechen() {
  const a = zustand.aktiv;
  if (!a) return;
  setzen({ aktiv: null });
  fortschrittSetzen(a.id, { schritt: a.schritt, pfad: aktuellerPfad(), status: 'pausiert', aktualisiert: jetzt() });
}

/** Fertig im letzten Schritt */
export function tourBeenden() {
  const a = zustand.aktiv;
  if (!a) return;
  setzen({ aktiv: null });
  fortschrittSetzen(a.id, { schritt: a.schritt, pfad: aktuellerPfad(), status: 'beendet', aktualisiert: jetzt() });
}

/** Tour ohne Spur beenden (Anleitung nicht mehr sichtbar) */
export function tourVerlassen() {
  const a = zustand.aktiv;
  if (!a) return;
  setzen({ aktiv: null });
  fortschrittSetzen(a.id, null);
}

/** Pausierten Stand verwerfen */
export function fortschrittVerwerfen(id: string) {
  if (zustand.aktiv?.id === id) setzen({ aktiv: null });
  fortschrittSetzen(id, null);
}

export function hilfeOeffnen() { setzen({ panelOffen: true }); }
export function hilfeSchliessen() { if (zustand.panelOffen) setzen({ panelOffen: false }); }
export function hilfeUmschalten() { setzen({ panelOffen: !zustand.panelOffen }); }

/** Tour ausblenden, ohne den Fortschritt zu ändern (z. B. Anleitung nicht freigeschaltet) */
export function tourAusblenden() { if (zustand.aktiv) setzen({ aktiv: null }); }

// ---------------------------------------------------------------------------
// Ereignis-API (verbindet die Tour-Ebene beim Einbinden, gibt die Abmeldung zurück)
// ---------------------------------------------------------------------------
export function ereignisseVerbinden(): () => void {
  const starten = (e: Event) => {
    const d = (e as CustomEvent<{ id?: string; ab?: number } | string | undefined>).detail;
    const id = typeof d === 'string' ? d : d?.id;
    const ab = typeof d === 'object' && d && typeof d.ab === 'number' ? d.ab : 0;
    if (id) void tourStarten(id, ab);
  };
  const fortsetzen = (e: Event) => { void tourFortsetzen((e as CustomEvent<{ id?: string } | undefined>).detail?.id); };
  const oeffnen = () => hilfeOeffnen();
  window.addEventListener('merkan:tour-starten', starten);
  window.addEventListener('merkan:tour-fortsetzen', fortsetzen);
  window.addEventListener('merkan:hilfe-oeffnen', oeffnen);
  return () => {
    window.removeEventListener('merkan:tour-starten', starten);
    window.removeEventListener('merkan:tour-fortsetzen', fortsetzen);
    window.removeEventListener('merkan:hilfe-oeffnen', oeffnen);
  };
}
